/**
 * DonutChart — кольцевая диаграмма с числом в центре и легендой
 * «квадрат · название …… значение · %» (виджеты «Структура портфеля»
 * и «Способы закупки», FR-4/FR-6). Рендер — recharts Pie поверх
 * ChartContainer (как pie-чарты «Аналитики»).
 */
import { Cell, Pie, PieChart } from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../../components/ui/chart';
import { fmt1 } from './format';

export interface DonutItem {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Готовая подпись значения в легенде и тултипе («7,4 млрд ₸», «52 дог.»). */
  valueLabel: string;
}

interface DonutChartProps {
  items: DonutItem[];
  centerValue: string;
  centerLabel: string;
  testId?: string;
}

export const DonutChart = ({
  items,
  centerValue,
  centerLabel,
  testId,
}: DonutChartProps) => {
  const total = items.reduce((acc, item) => acc + item.value, 0);
  const segments = items.filter((item) => item.value > 0);

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-5"
      data-testid={testId}
    >
      <div className="relative w-full max-w-[190px] shrink-0">
        <ChartContainer
          config={Object.fromEntries(
            segments.map((item) => [
              item.key,
              { label: item.label, color: item.color },
            ]),
          )}
          className="aspect-square"
        >
          <PieChart>
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Pie
              data={segments}
              dataKey="value"
              nameKey="label"
              innerRadius="62%"
              outerRadius="98%"
              strokeWidth={2}
            >
              {segments.map((item) => (
                <Cell key={item.key} fill={item.color} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
        <div
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center"
          aria-hidden="true"
        >
          <span className="text-xl font-extrabold tabular-nums leading-tight">
            {centerValue}
          </span>
          <span className="max-w-32 text-xs leading-snug text-text-muted">
            {centerLabel}
          </span>
        </div>
      </div>

      <ul className="flex min-w-56 flex-1 flex-col gap-2">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex items-baseline gap-2 text-sm"
            data-testid={testId ? `${testId}-${item.key}` : undefined}
          >
            <span
              className="size-2.5 shrink-0 self-center rounded-sm"
              style={{ backgroundColor: item.color }}
              aria-hidden="true"
            />
            <span className="min-w-0 text-text-muted">{item.label}</span>
            <span className="flex-1 border-b border-dotted border-surface-border" />
            <span className="whitespace-nowrap font-semibold tabular-nums">
              {item.valueLabel}
            </span>
            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-text-faint">
              {total > 0 ? `${fmt1((item.value / total) * 100)}%` : '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
